<script setup>
import { computed } from 'vue'
import { Head, usePage, Link } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PartnersLayout from '@/Layouts/PartnersLayout.vue'

// Render-function layout so the shared partners chrome renders in its wide
// variant. Inertia v3 invokes a 2-arg layout as `layout(h, child)`.
defineOptions({
  layout: (h, page) => h(PartnersLayout, { wide: true }, () => page)
})

const { t } = useI18n()
const page = usePage()
const base = computed(() => page.props.base || '')

defineProps({
  types:         { type: Array, default: () => [] },
  portal_logins: { type: Array, default: () => [] }
})

const steps = ['register', 'approve', 'manage']

function scrollToTypes() {
  document.getElementById('partner-types')?.scrollIntoView({ behavior: 'smooth' })
}
</script>

<template>
  <Head><title>{{ t('partners.index.head_title') }}</title></Head>

  <section class="hub-hero">
    <h1 class="hub-hero__title">{{ t('partners.index.title') }}</h1>
    <p class="hub-hero__subtitle">{{ t('partners.index.hero_subtitle') }}</p>
    <button
      type="button"
      class="hub-cta"
      @click="scrollToTypes"
    >
      {{ t('partners.index.hero_cta') }}
    </button>
  </section>

  <section class="hub-section">
    <h2 class="hub-section__title">{{ t('partners.index.how_title') }}</h2>
    <ol class="hub-steps">
      <li
        v-for="(step, i) in steps"
        :key="step"
        class="hub-step"
      >
        <span class="hub-step__num">{{ i + 1 }}</span>
        <span class="hub-step__title">{{ t(`partners.index.steps.${step}.title`) }}</span>
        <span class="hub-step__body">{{ t(`partners.index.steps.${step}.body`) }}</span>
      </li>
    </ol>
  </section>

  <section
    id="partner-types"
    class="hub-section"
  >
    <h2 class="hub-section__title">{{ t('partners.index.types_title') }}</h2>
    <p class="hub-section__lede">{{ t('partners.index.lede') }}</p>
    <div class="p-types">
      <Link
        v-for="ty in types"
        :key="ty"
        :href="`${base}/register?type=${ty}`"
        class="p-type"
      >
        <span class="p-type__name">{{ t(`partners.types.${ty}.name`) }}</span>
        <span class="p-type__desc">{{ t(`partners.types.${ty}.tagline`) }}</span>
      </Link>
    </div>
  </section>

  <section class="hub-section">
    <h2 class="hub-section__title">{{ t('partners.index.login_title') }}</h2>
    <div class="hub-logins">
      <a
        v-for="login in portal_logins"
        :key="login.key"
        :href="login.url"
        class="hub-login"
      >
        {{ t(`partners.logins.${login.key}`) }}
      </a>
    </div>
    <p class="hub-login__note">{{ t('partners.index.login_note') }}</p>
  </section>
</template>

<style scoped>
.hub-hero { text-align: center; padding: 0.5rem 0 1.5rem; border-bottom: 1px solid var(--line); margin-bottom: 1.5rem; }
.hub-hero__title { font-size: 1.625rem; font-weight: 700; color: var(--ink); margin: 0 0 0.5rem; line-height: 1.2; }
.hub-hero__subtitle { color: var(--inkSoft); margin: 0 auto 1.25rem; max-width: 34rem; font-size: 0.9375rem; line-height: 1.55; }
.hub-cta { display: inline-block; border: 0; cursor: pointer; padding: 0.7rem 1.4rem; border-radius: var(--radius-button, 0.625rem); background: var(--accent); color: var(--surface); font-size: 0.9375rem; font-weight: 600; transition: filter 150ms ease; }
.hub-cta:hover { filter: brightness(1.05); }

.hub-section { margin-bottom: 1.75rem; }
.hub-section__title { font-size: 1.0625rem; font-weight: 700; color: var(--ink); margin: 0 0 0.75rem; }
.hub-section__lede { color: var(--inkSoft); margin: 0 0 1rem; font-size: 0.9375rem; line-height: 1.5; }

.hub-steps { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.75rem; }
@media (min-width: 40rem) { .hub-steps { grid-template-columns: repeat(3, 1fr); } }
.hub-step { display: flex; flex-direction: column; gap: 0.3rem; padding: 1rem; border: 1px solid var(--line); border-radius: 0.75rem; background: var(--surfaceAlt, var(--surface)); }
.hub-step__num { display: inline-flex; align-items: center; justify-content: center; width: 1.5rem; height: 1.5rem; border-radius: 50%; background: var(--accentSoft, color-mix(in oklch, var(--accent) 14%, var(--surface))); color: var(--accent); font-size: 0.8125rem; font-weight: 700; }
.hub-step__title { font-weight: 600; color: var(--ink); font-size: 0.9375rem; }
.hub-step__body { font-size: 0.8125rem; color: var(--muted); line-height: 1.45; }

.p-types { display: grid; gap: 0.625rem; }
.p-type { display: flex; flex-direction: column; gap: 0.15rem; padding: 0.875rem 1rem; border: 1px solid var(--line); border-radius: 0.75rem; background: var(--surface); text-decoration: none; transition: border-color 150ms ease, transform 150ms ease; }
.p-type:hover { border-color: color-mix(in oklch, var(--accent) 60%, var(--line)); transform: translateY(-1px); }
.p-type__name { font-weight: 600; color: var(--ink); }
.p-type__desc { font-size: 0.8125rem; color: var(--muted); }

.hub-logins { display: grid; gap: 0.5rem; }
@media (min-width: 40rem) { .hub-logins { grid-template-columns: repeat(2, 1fr); } }
.hub-login { display: flex; align-items: center; justify-content: space-between; padding: 0.75rem 1rem; border: 1px solid var(--line); border-radius: 0.625rem; background: var(--surface); color: var(--ink); text-decoration: none; font-size: 0.9375rem; font-weight: 600; transition: border-color 150ms ease; }
.hub-login:hover { border-color: color-mix(in oklch, var(--accent) 60%, var(--line)); }
.hub-login__note { font-size: 0.8125rem; color: var(--muted); margin: 0.75rem 0 0; }
</style>
